import { Divider, IconButton, Stack, Typography } from '@mui/material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { FormProvider, useForm } from 'react-hook-form';
import { FinancingRequestDto } from 'tdc-web-backend/financing-requests/schemas';
import { useTranslation } from 'react-i18next';
import CustomModal from '../../../../../../../components/modal/CustomModal';
import { secondaryBlue } from '../../../../../../../utils/color';
import CustomButton from '../../../../../../../components/button/CustomButton';
import CustomInput from '../../../../../../../components/custom-input/CustomInput';
import useAuth from '../../../../../../../utils/hooks/useAuth';
import { useRefresh } from '../../../../../../../utils/hooks/crud-hooks/useRefresh';
import useCreate from '../../../../../../../utils/hooks/crud-hooks/useCreate';

interface INewRequestModalProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  projectId: string | undefined;
}

type FinancingRequestFormValues = {
  contactData: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
};

const NewRequestModal = ({ isOpen, setIsOpen, projectId }: INewRequestModalProps) => {
  const { t } = useTranslation();
  const {
    authData: { userData },
  } = useAuth();
  const refresh = useRefresh();

  const formMethods = useForm<FinancingRequestFormValues>({
    mode: 'onChange',
    defaultValues: {
      contactData: {
        firstName: userData?.firstName ?? '',
        lastName: userData?.lastName ?? '',
        email: userData?.email ?? '',
        phone: '',
      },
    },
  });

  const { formState } = formMethods;

  const { mutate, isLoading: isSubmitting } = useCreate<
    FinancingRequestDto,
    { contactData: FinancingRequestFormValues['contactData']; project: string }
  >({
    resource: 'financing-requests',
  });

  const onSubmit = (data: FinancingRequestFormValues) => {
    if (!projectId) return;
    mutate(
      {
        contactData: data.contactData,
        project: projectId,
      },
      {
        onSuccess: () => {
          formMethods.reset();
          setIsOpen(false);
          refresh();
        },
      },
    );
  };

  const handleCloseNewRequest = () => {
    formMethods.reset();
    setIsOpen(false);
  };

  return (
    <CustomModal
      open={isOpen}
      onClose={() => setIsOpen(false)}
      sx={{ minWidth: '600px', maxWidth: '840px' }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            {/* Header */}
            <Stack direction="row" spacing={2} alignItems="center">
              <IconButton
                sx={{
                  borderRadius: '4px',
                  bgcolor: 'secondaryBlue.100',
                  width: '3rem',
                  height: '3rem',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': { bgcolor: 'secondaryBlue.100' },
                }}
              >
                <AttachMoneyIcon sx={{ color: secondaryBlue[700] }} />
              </IconButton>
              <Typography variant="body2" fontWeight={600}>
                {t('financing.newRequest')}
              </Typography>
            </Stack>

            <Divider />

            {/* Description */}
            <Typography variant="body2" color="primaryDark.500">
              {t('financing.newRequestDescription')}
            </Typography>

            {/* Form fields */}
            <Stack spacing={3}>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <CustomInput
                  name="contactData.firstName"
                  label={t('common.firstName')}
                  required
                  input={<CustomInput.Text />}
                  sx={{ width: '50%' }}
                  disabled={isSubmitting}
                />
                <CustomInput
                  name="contactData.lastName"
                  label={t('common.lastName')}
                  required
                  input={<CustomInput.Text />}
                  sx={{ width: '50%' }}
                  disabled={isSubmitting}
                />
              </Stack>
              <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                <CustomInput
                  name="contactData.email"
                  label={t('common.email')}
                  required
                  type="email"
                  input={<CustomInput.Text />}
                  rules={{
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: t('common.invalidEmail'),
                    },
                  }}
                  sx={{ width: '50%' }}
                  disabled={isSubmitting}
                />

                <CustomInput
                  name="contactData.phone"
                  label={t('common.phone')}
                  placeholder={t('common.optional')}
                  input={<CustomInput.Text />}
                  sx={{ width: '50%' }}
                  disabled={isSubmitting}
                />
              </Stack>
            </Stack>

            <Divider />

            {/* Buttons */}
            <Stack spacing={2} direction="row" justifyContent="flex-end">
              <CustomButton
                variant="secondary"
                onClick={handleCloseNewRequest}
                disabled={isSubmitting}
              >
                {t('common.cancel')}
              </CustomButton>
              <CustomButton type="submit" disabled={!formState.isValid || isSubmitting}>
                {t('common.submit')}
              </CustomButton>
            </Stack>
          </Stack>
        </form>
      </FormProvider>
    </CustomModal>
  );
};

export default NewRequestModal;
